import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback, useContext, type ReactNode } from 'react';
import {
    Box, Skeleton, Text, Flex, HStack, VStack, IconButton, Icon, Tooltip as ChakraTooltip, Spinner,
    Button, Switch, FormLabel, FormControl,
    Popover, PopoverTrigger, PopoverContent, PopoverBody, Input, InputGroup, InputLeftElement, Portal,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
    useDisclosure,
} from '@chakra-ui/react';
import AppDrawer from '../../../components/AppDrawer';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, ReferenceLine, Legend, LabelList, Cell
} from 'recharts';
import { DomainStatItem, DomainType, DomainTimeseriesDataPoint, DomainTimeseriesResponse, NEGATIVE_METRICS, VendorGroupBy, ReportingComparison } from '../../../types/reporting';
import { getTimeseriesComparisonLabel } from '../shared/reportingComparisonLabels';
import { isReportingComparisonSupported } from '../shared/reportingComparisonUtils';
import { useDomainTimeseries } from '../../../hooks/reporting/useDomainTimeseries';
import { useDomainStats } from '../../../hooks/reporting/useDomainStats';
import { useReportingFilters } from '../ReportingFiltersContext';
import ExcludeEvsToggle from './ExcludeEvsToggle';
import { ChartMetricType, formatChartValue, formatTooltipValue, getMetricFormat, getMetricLabel, METRIC_GROUPS, VENDOR_METRIC_GROUPS, CHART_METRIC_OPTIONS, VENDOR_CHART_METRIC_OPTIONS, LOCATION_CHART_METRIC_OPTIONS, INSURANCE_CHART_METRIC_OPTIONS, OEM_CHART_METRIC_OPTIONS } from './ChartMetricSelector';
import { getBarChartYDomain, getBarChartYTicks } from '../../../utils/chartUtils';
import {
    chartValueFontSize,
    comparisonBarLabelListContent,
    verticalBarTopLabelListProps,
    yAxisTickFontSize,
    yAxisWidth,
} from '../shared/barValueLabels';
import MultiEntitySelector, { ENTITY_COLORS } from './MultiEntitySelector';
import { TbDownload, TbTrendingUp, TbTrendingDown, TbInfoCircle, TbSettings, TbChevronDown, TbCheck, TbSearch, TbEye } from 'react-icons/tb';
import {
    formatPeriodAxisLabel,
    formatPeriodTooltipLabel,
    getPartialPeriodLabel,
    resolveComparablePriorPeriodLabel,
    getTimeseriesPlotWidth,
    getTimeseriesXAxisLayout,
    isPartialReportingPeriod,
} from '../shared/dateUtils';
import { timeseriesXAxisTickRenderer } from '../shared/TimeseriesDateTick';
import { useChartContainerWidth } from '../shared/useChartContainerWidth';
import { ComparisonTooltipFooter } from '../shared/ComparisonTooltipFooter';
import { resolveComparablePriorMetricValue } from '../shared/reportingPriorValues';
import {
    shouldIncludeDomainTimeseriesDate,
} from '../shared/reportingTimeseriesVisibility';
import { exportToCsv } from '../../../utils/csvExport';
import { calculateMetricChange } from '../shared/metricChange';
import ReportingFullScreenShell from './ReportingFullScreenShell';
import { ReportingChartFullscreenButton } from '../shared/reportingChartFullscreen';
import SessionContext from '../../../contexts/SessionContext';
import { filterChartMetricsForInvoicing, isInvoicingGatedChartMetric, orgShowsInvoicingReportingMetrics } from '../shared/invoicingGatedMetrics';

interface TSVariantGroup {
    label: string;
    vendorLabel?: string;
    variants: { value: string; label: string; }[];
}

const TS_VARIANT_GROUPS: TSVariantGroup[] = [
    {
        label: 'Order Size', variants: [
            { value: 'avgOrderSize', label: 'Avg.' },
            { value: 'medianOrderSize', label: 'Median' },
        ]
    },
    {
        label: 'Gross Margin', vendorLabel: 'Discount', variants: [
            { value: 'avgGrossMarginPct', label: 'Avg.' },
            { value: 'medianGrossMarginPct', label: 'Median' },
        ]
    },
    {
        label: 'Gross Margin (w/o PM)', vendorLabel: 'Discount (w/o PM)', variants: [
            { value: 'avgGrossMarginPctWoPM', label: 'Avg.' },
            { value: 'medianGrossMarginPctWoPM', label: 'Median' },
        ]
    },
    {
        label: 'Missed Opportunity', variants: [
            { value: 'missedOpportunity', label: 'Total' },
            { value: 'avgMissedOpportunity', label: 'Avg.' },
            { value: 'medianMissedOpportunity', label: 'Median' },
        ]
    },
    {
        label: 'Part Value', variants: [
            { value: 'avgPartValue', label: 'Avg.' },
            { value: 'medianPartValue', label: 'Median' },
        ]
    },
    {
        label: 'Cost Variance', variants: [
            { value: 'totalCostVariance', label: 'Total' },
            { value: 'avgCostVariance', label: 'Avg.' },
            { value: 'medianCostVariance', label: 'Median' },
        ]
    },
    {
        label: 'Delivery Variance', variants: [
            { value: 'avgDeliveryVarianceDays', label: 'Avg.' },
            { value: 'medianDeliveryVarianceDays', label: 'Median' },
        ]
    },
    {
        label: 'List Price', variants: [
            { value: 'avgListPrice', label: 'Avg.' },
            { value: 'medianListPrice', label: 'Median' },
        ]
    },
    {
        label: '% OEM', variants: [
            { value: 'pctOemVolume', label: 'Volume' },
            { value: 'pctOemCount', label: 'Count' },
        ]
    },
    {
        label: '% LKQ', variants: [
            { value: 'pctLkqVolume', label: 'Volume' },
            { value: 'pctLkqCount', label: 'Count' },
        ]
    },
    {
        label: '% Aftermarket', variants: [
            { value: 'pctAftermarketVolume', label: 'Volume' },
            { value: 'pctAftermarketCount', label: 'Count' },
        ]
    },
];

function TSMetricPopoverSelect({ value, options, onChange, width = '220px', domain }: {
    value: string;
    options: Array<{ value: string; label: string; tooltip?: string; }>;
    onChange: (value: string) => void;
    width?: string;
    domain?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [popW, setPopW] = useState<number>(0);
    useEffect(() => { if (isOpen && triggerRef.current) setPopW(triggerRef.current.offsetWidth); }, [isOpen]);
    useEffect(() => { if (!isOpen) setSearch(''); }, [isOpen]);

    const isVendorDomain = domain === 'vendor';
    const allValues = new Set(options.map(m => m.value));
    const validGroups = TS_VARIANT_GROUPS.reduce<{ group: TSVariantGroup; representative: string; members: Set<string>; }[]>((acc, group) => {
        const available = group.variants.filter(v => allValues.has(v.value));
        if (available.length >= 2) {
            acc.push({ group: { ...group, variants: available }, representative: available[0].value, members: new Set(available.map(v => v.value)) });
        }
        return acc;
    }, []);
    const metricToGroup = new Map<string, typeof validGroups[0]>();
    validGroups.forEach(vg => { vg.members.forEach(m => metricToGroup.set(m, vg)); });

    const condensed: { value: string; label: string; }[] = [];
    const seenReps = new Set<string>();
    for (const opt of options) {
        const vg = metricToGroup.get(opt.value);
        if (vg) {
            if (!seenReps.has(vg.representative)) {
                seenReps.add(vg.representative);
                const label = isVendorDomain && vg.group.vendorLabel ? vg.group.vendorLabel : vg.group.label;
                condensed.push({ value: vg.representative, label });
            }
        } else {
            condensed.push({ value: opt.value, label: opt.label });
        }
    }
    const currentVg = metricToGroup.get(value);
    const dropdownValue = currentVg ? currentVg.representative : value;
    const selectedLabel = condensed.find(o => o.value === dropdownValue)?.label ?? 'Select Metric';
    const filtered = search ? condensed.filter(o => o.label?.toLowerCase().includes(search.toLowerCase())) : condensed;

    return (
        <Popover placement="bottom-start" isOpen={isOpen} onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)} matchWidth>
            <PopoverTrigger>
                <Button ref={triggerRef} variant="outline" w={width} h="36px" bg="white" borderColor="gray.300" borderRadius="md" fontWeight="normal" textAlign="left" justifyContent="space-between" px={3} _hover={{ borderColor: 'gray.400' }} rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={4} color="gray.500" />}>
                    <Text noOfLines={1} fontSize="sm" color="gray.800">{selectedLabel}</Text>
                </Button>
            </PopoverTrigger>
            <Portal>
                <PopoverContent w={popW ? `${popW}px` : width} maxH="320px" overflowY="auto" zIndex="popover" onClick={e => e.stopPropagation()}>
                    <PopoverBody p={2}>
                        <VStack align="stretch" spacing={1}>
                            <InputGroup size="sm" mb={1}>
                                <InputLeftElement pointerEvents="none"><Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3} /></InputLeftElement>
                                <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} bg="white" borderColor="gray.200" />
                            </InputGroup>
                            {filtered.map(opt => {
                                const active = opt.value === dropdownValue;
                                return (
                                    <Flex key={opt.value} align="center" px={2} py={1.5} borderRadius="md" cursor="pointer" bg={active ? '#E6F1FD' : 'transparent'} _hover={{ bg: active ? '#E6F1FD' : 'gray.50' }} onClick={() => { onChange(opt.value); setIsOpen(false); }}>
                                        <Text flex={1} fontSize="sm" color={active ? '#0071E3' : 'gray.700'} fontWeight={active ? 'medium' : 'normal'}>{opt.label}</Text>
                                        {active && <Icon as={TbCheck as React.ElementType} boxSize={4} color="#0071E3" />}
                                    </Flex>
                                );
                            })}
                            {filtered.length === 0 && <Text color="gray.500" textAlign="center" py={2} fontSize="sm">No matches</Text>}
                        </VStack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
}

interface DomainTimeseriesChartProps {
    domain: DomainType;
    startDate: string;
    endDate: string;
    entities: DomainStatItem[];
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    locationId?: string;
    defaultMetric?: ChartMetricType;
    preset?: string;
    comparison?: ReportingComparison;
    vendorGroupBy?: VendorGroupBy;
    onPrefetchVendorGroupBy?: (groupBy: VendorGroupBy) => void;
    /** When false, keep React Query cache but do not fetch. */
    enabled?: boolean;
}

const DOMAIN_LABELS: Record<DomainType, string> = {
    location: 'Locations',
    vendor: 'Vendors',
    insurance: 'Insurance',
    oem: 'OEMs',
};

const SUM_METRICS = [
    'orderCount', 'orderVolume', 'orderVolumeWoPM', 'orderCost', 'partsOrdered',
    'grossProfit', 'missedOpportunity', 'totalReturnCount', 'totalReturnValue',
    'totalCostVariance', 'quoteOpportunities', 'quotesSubmitted',
];

// Metrics that should be averaged when aggregating (percentage/rates)
const AVG_METRICS = [
    'avgListPrice', 'medianListPrice', 'avgListPriceWoPM', 'medianListPriceWoPM',
    'avgOrderSize', 'medianOrderSize',
    'avgGrossMarginPct', 'medianGrossMarginPct', 'p90GrossMarginPct', 'avgGrossMarginPctWoPM', 'medianGrossMarginPctWoPM',
    'grossMarginLossFromPM', 'pctAutoOrdered', 'pctAutoOrderEnabled',
    'partListPricePctOfOE', 'avgMissedOpportunity', 'medianMissedOpportunity',
    'pctPartsFlagged',
];

/** Variance avgs / pctPartsInvoiced are missing from backend aggregateByDate.
 *  Vendor org-wide "all" series carries them; other domains hide until entities are selected. */
const VARIANCE_AGGREGATE_UNSUPPORTED = new Set([
    'avgCostVariance',
    'medianCostVariance',
    'avgDeliveryVarianceDays',
    'medianDeliveryVarianceDays',
    'pctPartsInvoiced',
]);

/** Metrics present on DomainTimeseriesAggregate (safe to read from aggregateByDate). */
const AGGREGATE_BY_DATE_METRICS = new Set([
    'orderCount', 'orderVolume', 'orderVolumeWoPM', 'orderCost', 'partsOrdered',
    'grossProfit', 'grossProfitWoPM', 'missedOpportunity',
    'totalReturnCount', 'totalReturnValue', 'totalReturnPct',
    'partsWithEtaChange', 'avgEtaChangeDays', 'medianEtaChangeDays',
    'avgListPrice', 'medianListPrice', 'avgListPriceWoPM', 'medianListPriceWoPM',
    'avgOrderSize', 'medianOrderSize',
    'avgGrossMarginPct', 'medianGrossMarginPct', 'avgGrossMarginPctWoPM',
    'medianGrossMarginPctWoPM', 'grossMarginLossFromPM', 'p90GrossMarginPct',
    'avgMissedOpportunity', 'medianMissedOpportunity', 'partListPricePctOfOE',
    'pctAutoOrdered', 'pctAutoOrderEnabled', 'pctPartsFlagged',
]);

/**
 * Aggregate timeseries data by date (when showing "All" entities).
 * Prefer backend `aggregateByDate` for displayed metrics; this client fallback
 * still simple-averages some rates when the API map is absent.
 */
const aggregateByDate = (data: DomainTimeseriesDataPoint[]): DomainTimeseriesDataPoint[] => {
    const dateMap = new Map<string, { points: DomainTimeseriesDataPoint[], count: number; }>();

    data.forEach(point => {
        const existing = dateMap.get(point.date);
        if (existing) {
            existing.points.push(point);
            existing.count++;
        } else {
            dateMap.set(point.date, { points: [point], count: 1 });
        }
    });

    const aggregated: DomainTimeseriesDataPoint[] = [];

    dateMap.forEach((value, date) => {
        const { points, count } = value;

        const aggregatedPoint: DomainTimeseriesDataPoint = {
            date,
            domainId: 'all',
            domainName: 'All',
            orderCount: 0,
            orderVolume: 0,
            orderVolumeWoPM: 0,
            orderCost: 0,
            partsOrdered: 0,
            avgListPrice: 0,
            medianListPrice: 0,
            avgListPriceWoPM: 0,
            medianListPriceWoPM: 0,
            avgOrderSize: 0,
            medianOrderSize: 0,
            avgGrossMarginPct: 0,
            medianGrossMarginPct: 0,
            p90GrossMarginPct: 0,
            avgGrossMarginPctWoPM: 0,
            medianGrossMarginPctWoPM: 0,
            grossMarginLossFromPM: 0,
            partListPricePctOfOE: 0,
            missedOpportunity: 0,
            avgMissedOpportunity: 0,
            medianMissedOpportunity: 0,
            grossProfit: 0,
            pctAutoOrdered: 0,
            pctAutoOrderEnabled: 0,
            pctPartsFlagged: 0,
            totalReturnCount: 0,
            totalReturnValue: 0,
            totalReturnPct: 0,
            totalCostVariance: 0,
            avgCostVariance: 0,
            medianCostVariance: 0,
            avgDeliveryVarianceDays: 0,
            medianDeliveryVarianceDays: 0,
            pctPartsInvoiced: 0,
        };

        points.forEach(p => {
            SUM_METRICS.forEach(metric => {
                (aggregatedPoint as any)[metric] += (p as any)[metric] || 0;
            });
        });

        points.forEach(p => {
            AVG_METRICS.forEach(metric => {
                (aggregatedPoint as any)[metric] += ((p as any)[metric] || 0) / count;
            });
        });

        // Return % must be recomputed from summed totals, not averaged across entities
        aggregatedPoint.totalReturnPct = aggregatedPoint.partsOrdered > 0
            ? (aggregatedPoint.totalReturnCount / aggregatedPoint.partsOrdered) * 100
            : 0;

        aggregated.push(aggregatedPoint);
    });

    return aggregated.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

export function getAggregatePriorValue(
    aggregatePriorByDate: DomainTimeseriesResponse['aggregatePriorByDate'],
    date: string,
    metric: ChartMetricType,
): number | undefined {
    if (!AGGREGATE_BY_DATE_METRICS.has(metric)) return undefined;
    const value = aggregatePriorByDate?.[date]?.[metric as keyof NonNullable<DomainTimeseriesResponse['aggregatePriorByDate']>[string]];
    return typeof value === 'number' ? value : undefined;
}

export function getAggregateValue(
    aggregateByDate: DomainTimeseriesResponse['aggregateByDate'] | undefined,
    date: string,
    metric: ChartMetricType,
): number | undefined {
    if (!AGGREGATE_BY_DATE_METRICS.has(metric)) return undefined;
    const value = aggregateByDate?.[date]?.[metric as keyof DomainTimeseriesResponse['aggregateByDate'][string]];
    return typeof value === 'number' ? value : undefined;
}

export const CustomTooltip = ({ active, payload, format, comparison, granularity, metricLabel, metric, preset, periodStartDate, periodEndDate }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        const isPartial = isPartialReportingPeriod(data.date, granularity, periodEndDate, periodStartDate);
        const partialLabel = getPartialPeriodLabel(granularity);
        const currentPeriodLabel = formatPeriodTooltipLabel(data.date, granularity, periodEndDate, periodStartDate);

        const comparisonLabel = getTimeseriesComparisonLabel(preset, granularity) ?? 'vs prior';

        const filteredPayload = payload.filter((entry: any) => {
            const dataKey = entry.dataKey || '';
            return !dataKey.startsWith('prior_');
        });

        const priorPeriodLabel = comparison
            ? resolveComparablePriorPeriodLabel(
                data.date,
                granularity,
                comparison,
                data.priorDate,
                data.priorEndDate,
                preset,
                periodEndDate,
                periodStartDate,
            )
            : undefined;

        const tooltipMinW = Math.min(
            280,
            Math.max(
                180,
                Math.max(
                    String(currentPeriodLabel ?? '').length,
                    filteredPayload.reduce((max: number, entry: { name?: string; value?: number; }) => {
                        const rowLabel = String(entry.name ?? '');
                        const rowValue = formatTooltipValue(Number(entry.value ?? 0), format);
                        return Math.max(max, rowLabel.length + rowValue.length);
                    }, 0),
                ) * 7 + 48,
            ),
        );

        return (
            <Box
                bg="white"
                p={2.5}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                boxShadow="none"
                minW={`${tooltipMinW}px`}
            >
                <Text fontSize="xs" color="gray.500" mb={1}>
                    {currentPeriodLabel} {isPartial && <Text as="span" color="red.500" fontWeight="medium">({partialLabel})</Text>}
                </Text>
                {metricLabel && (
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{metricLabel}</Text>
                )}
                {comparison && priorPeriodLabel && (
                    <Text fontSize="xs" color="gray.500" mb={1}>
                        Prior period: {priorPeriodLabel}
                    </Text>
                )}
                <VStack align="stretch" spacing={1}>
                    {filteredPayload.map((entry: any, index: number) => {
                        const name = entry.name;
                        const value = entry.value;
                        const color = entry.color;

                        const priorKey = `prior_${name}`;
                        const priorFromData = entry.payload[priorKey];
                        const priorValue = resolveComparablePriorMetricValue(
                            typeof priorFromData === 'number' ? priorFromData : undefined,
                            {
                                comparison,
                                date: data.date,
                                priorDate: data.priorDate,
                                granularity,
                                preset,
                            },
                        );

                        const changeValue = entry.payload[`change_${name}`];
                        const change = typeof changeValue === 'number' ? changeValue : undefined;
                        const changeLabel = format === 'percent' ? 'pp' : '%';

                        const isPositive = change !== undefined && change >= 0;
                        const isNegativeMetric = NEGATIVE_METRICS.includes(metric);
                        let colorString = "gray.500";
                        if (change !== undefined) {
                            colorString = isNegativeMetric
                                ? (isPositive ? "red.500" : "green.500")
                                : (isPositive ? "green.500" : "red.500");
                        }

                        return (
                            <Box key={index}>
                                <Flex justify="space-between" align="center" gap={3} mb={0.5}>
                                    <HStack spacing={2}>
                                        <Box w={2} h={2} bg={color} borderRadius="sm" />
                                        <Text fontSize="xs" color="gray.600" fontWeight="medium">{name}</Text>
                                    </HStack>
                                    <Text fontSize="sm" fontWeight="bold" color="#0071E3">
                                        {formatTooltipValue(value, format)}
                                    </Text>
                                </Flex>
                                {comparison ? (
                                    <Flex justify="flex-end" align="center" gap={1.5}>
                                        {comparison && priorValue !== undefined && priorValue !== null && (
                                            <Text fontSize="xs" color="gray.500">
                                                Prior: {formatTooltipValue(priorValue, format)}
                                            </Text>
                                        )}
                                        {change !== undefined && (
                                            <Flex align="center" gap={0.5}>
                                                <Icon
                                                    as={isPositive ? TbTrendingUp as React.ElementType : TbTrendingDown as React.ElementType}
                                                    color={colorString}
                                                    boxSize={2.5}
                                                />
                                                <Text
                                                    fontSize="xs"
                                                    fontWeight="medium"
                                                    color={colorString}
                                                >
                                                    {Math.abs(change).toFixed(1)}{changeLabel}
                                                    <Text as="span" color="gray.400" ml={1} fontWeight="normal">
                                                        {comparisonLabel}
                                                    </Text>
                                                </Text>
                                            </Flex>
                                        )}
                                        <ComparisonTooltipFooter
                                            comparison={comparison}
                                            change={change}
                                            priorValue={priorValue}
                                            priorPeriodLabel={priorPeriodLabel}
                                        />
                                    </Flex>
                                ) : null}
                            </Box>
                        );
                    })}
                </VStack>
            </Box>
        );
    }
    return null;
};

const TOOLTIP_OFFSET = 10;
const TOOLTIP_EDGE_PADDING = 4;

function estimateDomainTooltipHeight(entityCount: number, hasComparison: boolean): number {
    const headerHeight = 56 + (hasComparison ? 18 : 0);
    const rowHeight = hasComparison ? 40 : 26;
    const rowSpacing = Math.max(0, entityCount - 1) * 4;
    const padding = 20;
    return headerHeight + entityCount * rowHeight + rowSpacing + padding + 12;
}

function computeClampedTooltipY(
    coordinate: { x?: number; y?: number; } | undefined,
    viewBox: { x?: number; y?: number; width?: number; height?: number; } | undefined,
    entityCount: number,
    hasComparison: boolean,
): number | undefined {
    if (coordinate?.y == null || !viewBox) return undefined;

    const height = estimateDomainTooltipHeight(entityCount, hasComparison);
    const minY = (viewBox.y ?? 0) + TOOLTIP_EDGE_PADDING;
    const maxY = (viewBox.y ?? 0) + (viewBox.height ?? 0) - height - TOOLTIP_EDGE_PADDING;

    if (maxY < minY) return minY;

    let y = coordinate.y - height - TOOLTIP_OFFSET;
    if (y < minY) {
        y = coordinate.y + TOOLTIP_OFFSET;
    }

    return Math.max(minY, Math.min(maxY, y));
}

const ClampingTooltipBridge = ({
    onPositionChange,
    format,
    comparison,
    granularity,
    metricLabel,
    metric,
    preset,
    periodStartDate,
    periodEndDate,
    active,
    coordinate,
    viewBox,
    payload,
    label,
}: any) => {
    const lastReportedYRef = useRef<number | undefined>();

    const entityCount = useMemo(() => {
        if (!payload?.length) return 1;
        return payload.filter((entry: { dataKey?: string; }) => !String(entry.dataKey ?? '').startsWith('prior_')).length || 1;
    }, [payload]);

    const coordY = coordinate?.y;
    const viewBoxY = viewBox?.y;
    const viewBoxHeight = viewBox?.height;

    const clampedY = useMemo(
        () => computeClampedTooltipY(
            coordY == null ? undefined : { y: coordY },
            viewBox == null ? undefined : { y: viewBoxY, height: viewBoxHeight },
            entityCount,
            !!comparison,
        ),
        [coordY, viewBox, viewBoxY, viewBoxHeight, entityCount, comparison],
    );

    useLayoutEffect(() => {
        const nextY = !active || clampedY == null ? undefined : clampedY;
        if (lastReportedYRef.current === nextY) return;
        lastReportedYRef.current = nextY;
        onPositionChange(nextY === undefined ? undefined : { y: nextY });
    }, [active, clampedY, onPositionChange]);

    return (
        <CustomTooltip
            active={active}
            payload={payload}
            label={label}
            format={format}
            comparison={comparison}
            granularity={granularity}
            metricLabel={metricLabel}
            metric={metric}
            preset={preset}
            periodStartDate={periodStartDate}
            periodEndDate={periodEndDate}
        />
    );
};

const DomainTimeseriesChart = ({
    domain,
    startDate,
    endDate,
    entities,
    granularity = 'week',
    locationId,
    defaultMetric = 'orderVolume',
    preset,
    comparison,
    vendorGroupBy = 'vendor',
    onPrefetchVendorGroupBy,
    enabled = true,
}: DomainTimeseriesChartProps) => {
    const sessionContext = useContext(SessionContext);
    const invoicingEnabled = orgShowsInvoicingReportingMetrics(sessionContext);
    const { excludeEvs } = useReportingFilters();
    const [metric, setMetric] = useState<ChartMetricType>(defaultMetric);
    const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
    const [tooltipPosition, setTooltipPosition] = useState<{ y: number; } | undefined>();
    const tsSettingsDrawerRaw = useDisclosure();

    const handleTooltipPositionChange = useCallback((position: { y: number; } | undefined) => {
        setTooltipPosition((prev) => {
            if (prev?.y === position?.y) return prev;
            return position;
        });
    }, []);

    const [tsShowComparison, setTsShowComparison] = useState<boolean>(true);
    const [tsVendorGroupBy, setTsVendorGroupBy] = useState<VendorGroupBy>(vendorGroupBy);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [chartWidthRef, chartContainerWidth] = useChartContainerWidth();

    const comparisonSupported = isReportingComparisonSupported(preset);

    useEffect(() => {
        if (!comparisonSupported) {
            setTsShowComparison(false);
        }
    }, [comparisonSupported]);

    useEffect(() => {
        if (!invoicingEnabled && isInvoicingGatedChartMetric(metric)) {
            setMetric('orderVolume');
        }
    }, [invoicingEnabled, metric]);

    const effectiveTsShowComparison = tsShowComparison && comparisonSupported;

    const tsDrawerSnapshotRef = useRef<{
        metric: ChartMetricType;
        selectedEntityIds: string[];
        tsShowComparison: boolean;
        tsVendorGroupBy: VendorGroupBy;
    } | null>(null);

    const tsSettingsDrawer = useMemo(() => ({
        isOpen: tsSettingsDrawerRaw.isOpen,
        onClose: tsSettingsDrawerRaw.onClose,
        onOpen: () => {
            tsDrawerSnapshotRef.current = {
                metric,
                selectedEntityIds: [...selectedEntityIds],
                tsShowComparison,
                tsVendorGroupBy,
            };
            tsSettingsDrawerRaw.onOpen();
        },
    }), [tsSettingsDrawerRaw, metric, selectedEntityIds, tsShowComparison, tsVendorGroupBy]);

    const handleTsDrawerSave = useCallback(() => {
        tsDrawerSnapshotRef.current = null;
        tsSettingsDrawer.onClose();
    }, [tsSettingsDrawer]);

    const handleTsDrawerDiscard = useCallback(() => {
        const snap = tsDrawerSnapshotRef.current;
        if (snap) {
            setMetric(snap.metric);
            setSelectedEntityIds(snap.selectedEntityIds);
            setTsShowComparison(snap.tsShowComparison);
            setTsVendorGroupBy(snap.tsVendorGroupBy);
        }
        tsDrawerSnapshotRef.current = null;
        tsSettingsDrawer.onClose();
    }, [tsSettingsDrawer]);

    // Reset Compare when this chart's Group by changes so stale preference-tier IDs
    // are not used after switching to vendor ObjectIds (and vice versa).
    useEffect(() => {
        if (domain !== 'vendor') return;
        setSelectedEntityIds([]);
        if (tsDrawerSnapshotRef.current) {
            tsDrawerSnapshotRef.current = {
                ...tsDrawerSnapshotRef.current,
                selectedEntityIds: [],
            };
        }
    }, [domain, tsVendorGroupBy]);

    const timeseriesGroupBy = useMemo((): VendorGroupBy | undefined => {
        if (domain !== 'vendor') return undefined;
        if (tsVendorGroupBy === 'preference' && selectedEntityIds.length > 0) {
            return 'preference';
        }
        return 'vendor';
    }, [domain, tsVendorGroupBy, selectedEntityIds.length]);

    const effectiveTsComparison = effectiveTsShowComparison ? comparison : undefined;

    const isVendor = domain === 'vendor';
    const isVendorByPreference = isVendor && tsVendorGroupBy === 'preference';
    const isLocation = domain === 'location';
    const isInsurance = domain === 'insurance';
    const isOEM = domain === 'oem';

    const compareEntityLabels = useMemo(() => {
        if (isVendorByPreference) {
            return {
                section: 'Compare Preference Tiers',
                allAggregated: 'Showing all vendors (aggregated)',
                selector: {
                    all: 'All Preference Tiers',
                    singular: 'Preference Tier',
                    plural: 'Preference Tiers',
                },
            };
        }
        return {
            section: `Compare ${DOMAIN_LABELS[domain]}`,
            allAggregated: `Showing all ${DOMAIN_LABELS[domain].toLowerCase()} (aggregated)`,
            selector: undefined as { all: string; singular: string; plural: string; } | undefined,
        };
    }, [domain, isVendorByPreference]);

    // Automation metrics that are not available from domain timeseries endpoint for location/insurance
    const automationMetrics = ['pctAutoOrdered', 'pctAutoOrderEnabled', 'pctPartsFlagged'];

    // If current metric is an automation metric and we're on location/insurance domain, reset to default
    // Quote rate is vendor + groupBy vendor only (not preference / not org-wide aggregate)
    // Variance avgs / pctPartsInvoiced need entity selection outside vendor org-wide "all" series
    const hideVarianceInAllView = selectedEntityIds.length === 0 && !isVendor;
    const effectiveMetric = (isLocation || isInsurance) && automationMetrics.includes(metric)
        ? 'orderVolume'
        : (metric === 'quoteRate' && (!isVendor || isVendorByPreference || selectedEntityIds.length === 0))
            ? 'orderVolume'
            : (hideVarianceInAllView && VARIANCE_AGGREGATE_UNSUPPORTED.has(metric))
                ? 'orderVolume'
                : (!invoicingEnabled && isInvoicingGatedChartMetric(metric))
                    ? 'orderVolume'
                    : metric;

    const format = getMetricFormat(effectiveMetric, isVendor, isLocation, isInsurance, isOEM);
    const metricLabel = getMetricLabel(effectiveMetric, isVendor, isLocation, isInsurance, isOEM);

    // Get tooltip for the current metric
    const metricOptions = useMemo(() => {
        const base = isVendor
            ? VENDOR_CHART_METRIC_OPTIONS
            : isLocation
                ? LOCATION_CHART_METRIC_OPTIONS
                : isInsurance
                    ? INSURANCE_CHART_METRIC_OPTIONS
                    : isOEM
                        ? OEM_CHART_METRIC_OPTIONS
                        : CHART_METRIC_OPTIONS;
        // Quote rate is not on preference grouping or org-wide aggregate series
        const hideQuoteRate = !isVendor || isVendorByPreference || selectedEntityIds.length === 0;
        const hideVarianceAvgs = selectedEntityIds.length === 0 && !isVendor;
        return filterChartMetricsForInvoicing(base, invoicingEnabled).filter((o) => {
            if (hideQuoteRate && o.value === 'quoteRate') return false;
            if (hideVarianceAvgs && VARIANCE_AGGREGATE_UNSUPPORTED.has(o.value)) return false;
            return true;
        });
    }, [isVendor, isLocation, isInsurance, isOEM, isVendorByPreference, selectedEntityIds.length, invoicingEnabled]);
    const currentMetricOption = metricOptions.find(o => o.value === effectiveMetric);
    const metricTooltip = currentMetricOption?.tooltip;

    // Variant group logic for condensed metric selection
    const tsActiveVariantGroup = useMemo(() => {
        const allValues = new Set(metricOptions.map((m: any) => m.value));
        for (const group of TS_VARIANT_GROUPS) {
            const available = group.variants.filter(v => allValues.has(v.value));
            if (available.length >= 2) {
                const members = new Set(available.map(v => v.value));
                if (members.has(effectiveMetric)) {
                    return { ...group, variants: available };
                }
            }
        }
        return null;
    }, [effectiveMetric, metricOptions]);

    const handleTsCondensedMetricChange = useCallback((selectedValue: string) => {
        const allValues = new Set(metricOptions.map((m: any) => m.value));
        for (const group of TS_VARIANT_GROUPS) {
            const available = group.variants.filter(v => allValues.has(v.value));
            if (available.length >= 2 && available[0].value === selectedValue) {
                setMetric(available[0].value as ChartMetricType);
                return;
            }
        }
        setMetric(selectedValue as ChartMetricType);
    }, [metricOptions]);

    // Fetch timeseries data.
    // No selection → omit domainIds (vendor Total returns one org-wide aggregate series).
    // Selection (max 5) → fetch only those entities.
    const { data: timeseriesData, isLoading, isFetching } = useDomainTimeseries(domain, startDate, endDate, {
        granularity,
        locationId,
        preset,
        comparison,
        excludeEvs,
        groupBy: timeseriesGroupBy,
        domainIds: selectedEntityIds.length > 0 ? selectedEntityIds : undefined,
        enabled,
    });

    const needsSeparateCompareStats = isVendor && (
        tsVendorGroupBy === 'preference'
        || (vendorGroupBy !== undefined && tsVendorGroupBy !== vendorGroupBy)
    );

    const { data: vendorCompareStats } = useDomainStats(domain, startDate, endDate, {
        locationId,
        preset,
        comparison,
        excludeEvs,
        groupBy: isVendor ? tsVendorGroupBy : undefined,
        enabled: enabled && needsSeparateCompareStats,
    });

    const compareEntities = isVendor ? (vendorCompareStats?.items ?? entities) : entities;

    // Zero out MO metrics for insurance entities that don't have partstraderRequired
    const moIneligibleIds = useMemo(() => {
        if (domain !== 'insurance') return new Set<string>();
        return new Set(
            entities.filter(e => e.missedOpportunityEligible === false).map(e => e.id)
        );
    }, [domain, entities]);

    const filteredTimeseriesData = useMemo(() => {
        const data = timeseriesData?.data;
        if (!data || moIneligibleIds.size === 0) return data;
        return data.map(point => {
            if (!moIneligibleIds.has(point.domainId)) return point;
            return {
                ...point,
                missedOpportunity: 0,
                avgMissedOpportunity: 0,
                medianMissedOpportunity: 0,
            };
        });
    }, [timeseriesData?.data, moIneligibleIds]);

    // Determine if this metric is part of a group (for chart title)
    const metricGroups = isVendor ? VENDOR_METRIC_GROUPS : METRIC_GROUPS;
    const activeGroup = metricGroups.find(g =>
        (g.avg === metric || g.median === metric)
    );
    const chartTitleLabel = activeGroup ? activeGroup.label : metricLabel;

    // Get chart title based on selection
    const chartTitle = `${chartTitleLabel} Over Time`;
    // Calculate "All" aggregated data.
    // Vendor Total with no selection already returns one series (domainId "all") — use as-is.
    const allAggregatedData = useMemo(() => {
        if (!filteredTimeseriesData || filteredTimeseriesData.length === 0) return [];
        const alreadyAggregated = filteredTimeseriesData.every(point => point.domainId === 'all');
        if (alreadyAggregated) {
            return [...filteredTimeseriesData].sort(
                (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
            );
        }
        return aggregateByDate(filteredTimeseriesData);
    }, [filteredTimeseriesData]);

    // Get entity names for legend (needed before chartData)
    const entityNames = useMemo(() => {
        if (selectedEntityIds.length === 0) return ['All'];
        return selectedEntityIds.map(id => {
            const entity = compareEntities.find(e => e.id === id);
            return entity?.name || id;
        });
    }, [selectedEntityIds, compareEntities]);

    // Process data for chart based on selection
    const chartData = useMemo(() => {
        const data = filteredTimeseriesData;

        if (!data || data.length === 0) return [];

        const includePriorOnlyBuckets = effectiveTsShowComparison && !!comparison;
        const visibilityOptions = { includePriorOnlyBuckets };

        // Get all unique dates
        const allDates = Array.from(new Set(data.map(p => p.date))).sort(
            (a, b) => new Date(a).getTime() - new Date(b).getTime()
        ).filter((date) => shouldIncludeDomainTimeseriesDate(
            date,
            data,
            selectedEntityIds,
            visibilityOptions,
        ));

        if (selectedEntityIds.length === 0) {
            // Show "All" aggregated data — prefer backend weighted aggregates when present
            return allAggregatedData
                .filter((point) => shouldIncludeDomainTimeseriesDate(
                    point.date,
                    data,
                    selectedEntityIds,
                    visibilityOptions,
                ))
                .map((point) => {
                const datePoints = data.filter(p => p.date === point.date);
                const priorDate = datePoints.find(p => p.priorDate)?.priorDate;
                const backendCurrent = getAggregateValue(
                    timeseriesData?.aggregateByDate,
                    point.date,
                    effectiveMetric,
                );
                const currentValue = backendCurrent ?? (point as any)[effectiveMetric] ?? 0;

                // Prefer backend aggregate prior; else sum/read from point.prior
                // (totalCostVariance client sum, or vendor org-wide "all" series).
                let rawPrior: number | undefined = getAggregatePriorValue(
                    timeseriesData?.aggregatePriorByDate,
                    point.date,
                    effectiveMetric,
                );
                if (rawPrior === undefined) {
                    if (AGGREGATE_BY_DATE_METRICS.has(effectiveMetric)) {
                        // leave undefined — metric belongs on aggregate map only
                    } else if (datePoints.every(p => p.domainId === 'all') && datePoints.length === 1) {
                        const priorMetric = datePoints[0].prior?.[effectiveMetric as keyof NonNullable<DomainTimeseriesDataPoint['prior']>];
                        rawPrior = typeof priorMetric === 'number' ? priorMetric : undefined;
                    } else if (effectiveMetric === 'totalCostVariance') {
                        let sum = 0;
                        let any = false;
                        datePoints.forEach((p) => {
                            const v = p.prior?.totalCostVariance;
                            if (typeof v === 'number') {
                                sum += v;
                                any = true;
                            }
                        });
                        rawPrior = any ? sum : undefined;
                    } else {
                        const priorMetric = point.prior?.[effectiveMetric as keyof NonNullable<DomainTimeseriesDataPoint['prior']>];
                        rawPrior = typeof priorMetric === 'number' ? priorMetric : undefined;
                    }
                }

                const priorValue = resolveComparablePriorMetricValue(
                    rawPrior,
                    {
                        comparison: effectiveTsComparison,
                        date: point.date,
                        priorDate,
                        granularity,
                        preset,
                    },
                );
                const changeAll = calculateMetricChange(currentValue, priorValue, effectiveMetric);

                return {
                    ...point,
                    [effectiveMetric]: currentValue,
                    displayDate: formatPeriodAxisLabel(point.date, granularity, endDate, startDate),
                    priorDate,
                    All: currentValue,
                    prior_All: priorValue,
                    change_All: changeAll,
                };
            });
        }

        // Build comparison data - one row per date with columns for each entity
        return allDates.map((date) => {
            const row: Record<string, any> = {
                date,
                displayDate: formatPeriodAxisLabel(date, granularity, endDate, startDate),
            };

            // Add value for each selected entity
            selectedEntityIds.forEach(entityId => {
                const entityPoint = data.find(p => p.date === date && p.domainId === entityId);
                const entity = compareEntities.find(e => e.id === entityId);
                const key = entity?.name || entityId;

                if (entityPoint?.priorDate) {
                    row.priorDate = entityPoint.priorDate;
                }

                // Get current value
                row[key] = entityPoint ? (entityPoint as any)[effectiveMetric] || 0 : 0;

                // Get prior value from embedded prior data in the entity point
                // Backend embeds prior as: dataPoint.prior = { orderVolume: ..., avgGrossMarginPct: ... }
                const priorData = entityPoint ? (entityPoint as any).prior : undefined;
                const rawPrior = priorData ? priorData[effectiveMetric] : undefined;
                row[`prior_${key}`] = resolveComparablePriorMetricValue(
                    typeof rawPrior === 'number' ? rawPrior : undefined,
                    {
                        comparison: effectiveTsComparison,
                        date,
                        priorDate: entityPoint?.priorDate,
                        granularity,
                        preset,
                    },
                );
                const changeData = entityPoint ? (entityPoint as any).change : undefined;
                row[`change_${key}`] = changeData ? changeData[effectiveMetric] : undefined;
            });

            return row;
        });
    }, [filteredTimeseriesData, selectedEntityIds, effectiveMetric, compareEntities, allAggregatedData, timeseriesData?.aggregateByDate, timeseriesData?.aggregatePriorByDate, granularity, startDate, endDate, effectiveTsComparison, preset, effectiveTsShowComparison, comparison]);

    const canShowPriorBars = effectiveTsShowComparison && selectedEntityIds.length <= 1;
    const canOfferPriorPeriodToggle = comparisonSupported && selectedEntityIds.length <= 1;
    const hasComparisonBarData = canShowPriorBars && chartData.some(row =>
        entityNames.some(name => {
            const val = (row as Record<string, any>)[`prior_${name}`];
            return typeof val === 'number' && !isNaN(val);
        })
    );

    // Extract all values being displayed on the chart for calculating y-axis bounds
    const displayedValues = useMemo(() => {
        const values = chartData.flatMap(row =>
            entityNames.map(name => (row as Record<string, any>)[name] || 0)
        ).filter(v => !isNaN(v));

        if (hasComparisonBarData) {
            const priorValues = chartData.flatMap(row =>
                entityNames.map(name => (row as Record<string, any>)[`prior_${name}`])
            ).filter((v): v is number => typeof v === 'number' && !isNaN(v));
            values.push(...priorValues);
        }

        return values;
    }, [chartData, entityNames, hasComparisonBarData]);

    const handleDownload = () => {
        if (!chartData || chartData.length === 0) return;
        exportToCsv(chartData, `${chartTitle.replace(/\s+/g, '_').toLowerCase()}_data`);
    };

    const renderClampedTooltip = useCallback((props: any) => (
        <ClampingTooltipBridge
            {...props}
            format={format}
            comparison={effectiveTsComparison}
            granularity={granularity}
            metricLabel={metricLabel}
            metric={effectiveMetric}
            preset={preset}
            periodStartDate={startDate}
            periodEndDate={endDate}
            onPositionChange={handleTooltipPositionChange}
        />
    ), [format, effectiveTsComparison, granularity, metricLabel, effectiveMetric, preset, startDate, endDate, handleTooltipPositionChange]);

    const renderTsVendorGroupByControl = () => (
        <HStack spacing={0}>
            {([
                { value: 'vendor' as VendorGroupBy, label: 'Total' },
                { value: 'preference' as VendorGroupBy, label: 'By Preference' },
            ]).map((opt, i, arr) => {
                const isActive = tsVendorGroupBy === opt.value;
                const nextIsActive = i < arr.length - 1 && arr[i + 1].value === tsVendorGroupBy;
                return (
                    <Button
                        key={opt.value}
                        size="sm"
                        h="32px"
                        flex={1}
                        borderRadius={0}
                        borderLeftRadius={i === 0 ? 'md' : 0}
                        borderRightRadius={i === arr.length - 1 ? 'md' : 0}
                        border="1px solid"
                        borderColor={isActive ? '#0071E3' : 'gray.200'}
                        borderLeftColor={i > 0 ? (isActive ? '#0071E3' : 'transparent') : undefined}
                        borderRightColor={nextIsActive ? 'transparent' : undefined}
                        ml={i > 0 ? '-1px' : undefined}
                        zIndex={isActive ? 1 : 0}
                        bg={isActive ? '#E6F1FD' : 'white'}
                        color={isActive ? '#0071E3' : 'gray.600'}
                        fontWeight={isActive ? 'semibold' : 'normal'}
                        fontSize="sm"
                        _hover={{ bg: isActive ? '#E6F1FD' : 'gray.50' }}
                        onMouseEnter={() => {
                            if (!isActive) onPrefetchVendorGroupBy?.(opt.value);
                        }}
                        onClick={() => setTsVendorGroupBy(opt.value)}
                    >
                        {opt.label}
                    </Button>
                );
            })}
        </HStack>
    );

    const tsSettingsDrawerContent = (
        <AppDrawer
            isOpen={tsSettingsDrawer.isOpen}
            onClose={handleTsDrawerDiscard}
            title="Chart Settings"
            size="md"
            bodyProps={{ pt: 4 }}
            footer={
                <>
                    <Button size="action" variant="outlineGray" onClick={handleTsDrawerDiscard}>
                        Cancel
                    </Button>
                    <Button size="action" bg="#0071E3" color="white" _hover={{ bg: '#3544a0' }} onClick={handleTsDrawerSave}>
                        Save
                    </Button>
                </>
            }
        >
            <VStack spacing={6} align="stretch">
                        <Box bg="gray.50" borderRadius="md" p={3} border="1px solid" borderColor="gray.300">
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">{chartTitle}</Text>
                            {metricTooltip && <Text fontSize="xs" color="gray.500" mt={1}>{metricTooltip}</Text>}
                        </Box>

                        <Accordion allowMultiple defaultIndex={isVendor ? [0, 1, 2, 3] : [0, 1, 2]}>
                            {/* Metric Section */}
                            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={2} mb={3}>
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Metric</Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        <VStack spacing={4} align="stretch">
                                            <FormControl>
                                                <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Metric</FormLabel>
                                                <TSMetricPopoverSelect
                                                    value={effectiveMetric}
                                                    options={metricOptions}
                                                    onChange={handleTsCondensedMetricChange}
                                                    width="100%"
                                                    domain={domain}
                                                />
                                            </FormControl>
                                            {tsActiveVariantGroup && (
                                                <FormControl>
                                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
                                                        {tsActiveVariantGroup.variants[0].label === 'Volume' ? 'Measure by' : 'Aggregation'}
                                                    </FormLabel>
                                                    <HStack spacing={0}>
                                                        {tsActiveVariantGroup.variants.map((v, i) => {
                                                            const isActive = v.value === effectiveMetric;
                                                            const nextIsActive = i < tsActiveVariantGroup.variants.length - 1 && tsActiveVariantGroup.variants[i + 1].value === effectiveMetric;
                                                            return (
                                                                <Button
                                                                    key={v.value}
                                                                    size="sm"
                                                                    h="32px"
                                                                    flex={1}
                                                                    borderRadius={0}
                                                                    borderLeftRadius={i === 0 ? 'md' : 0}
                                                                    borderRightRadius={i === tsActiveVariantGroup.variants.length - 1 ? 'md' : 0}
                                                                    border="1px solid"
                                                                    borderColor={isActive ? '#0071E3' : 'gray.200'}
                                                                    borderLeftColor={i > 0 ? (isActive ? '#0071E3' : 'transparent') : undefined}
                                                                    borderRightColor={nextIsActive ? 'transparent' : undefined}
                                                                    ml={i > 0 ? '-1px' : undefined}
                                                                    zIndex={isActive ? 1 : 0}
                                                                    bg={isActive ? '#E6F1FD' : 'white'}
                                                                    color={isActive ? '#0071E3' : 'gray.600'}
                                                                    fontWeight={isActive ? 'semibold' : 'normal'}
                                                                    fontSize="sm"
                                                                    _hover={{ bg: isActive ? '#E6F1FD' : 'gray.50' }}
                                                                    onClick={() => setMetric(v.value as ChartMetricType)}
                                                                >
                                                                    {v.label}
                                                                </Button>
                                                            );
                                                        })}
                                                    </HStack>
                                                </FormControl>
                                            )}
                                        </VStack>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>

                            {/* Group by Section - vendor only */}
                            {isVendor && (
                                <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={1.75} mb={3}>
                                    <AccordionItem border="none">
                                        <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                            <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Group by</Text>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel px={4} pb={4} pt={1}>
                                            {renderTsVendorGroupByControl()}
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Box>
                            )}

                            {/* Compare Section */}
                            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={1.5} mb={3}>
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">{compareEntityLabels.section}</Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        <FormControl>
                                            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
                                                {selectedEntityIds.length === 0
                                                    ? compareEntityLabels.allAggregated
                                                    : `${selectedEntityIds.length} selected`}
                                            </FormLabel>
                                            <MultiEntitySelector
                                                domain={domain}
                                                entities={compareEntities}
                                                selectedIds={selectedEntityIds}
                                                onChange={setSelectedEntityIds}
                                                width="100%"
                                                usePortal
                                                labels={compareEntityLabels.selector}
                                            />
                                        </FormControl>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>

                            {/* Configuration Section */}
                            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="visible" position="relative" zIndex={1}>
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">Configuration</Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        <VStack spacing={4} align="stretch">
                                            {canOfferPriorPeriodToggle && (
                                                <Flex align="center" justify="space-between" cursor="pointer" onClick={() => setTsShowComparison(v => !v)}>
                                                    <HStack spacing={3}>
                                                        <Icon as={TbEye as React.ElementType} boxSize={5} color="gray.500" />
                                                        <Box>
                                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                                Show prior period
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                Display change indicators vs. previous period
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                    <Switch isChecked={tsShowComparison} pointerEvents="none" colorScheme="blue" size="md" />
                                                </Flex>
                                            )}
                                            <ExcludeEvsToggle />
                                        </VStack>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>
                        </Accordion>
            </VStack>
        </AppDrawer>
    );

    const tsSettingsButton = (
        <IconButton
            aria-label="Chart settings"
            icon={<Icon as={TbSettings as React.ElementType} boxSize={4} />}
            size="sm"
            h="32px"
            w="32px"
            variant="outline"
            borderColor="gray.300"
            color="gray.600"
            borderRadius="md"
            _hover={{ bg: 'gray.50' }}
            onClick={tsSettingsDrawer.onOpen}
        />
    );

    const metricSelectorRow = (
        <Flex align="center" gap={2} flexShrink={0}>
            {tsSettingsButton}
            <ChakraTooltip label="Export to CSV" hasArrow placement="top">
                <IconButton
                    aria-label="Export to CSV"
                    icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                    size="sm"
                    h="32px"
                    w="32px"
                    variant="outline"
                    borderColor="gray.300"
                    color="gray.600"
                    borderRadius="md"
                    _hover={{ bg: 'gray.50' }}
                    onClick={handleDownload}
                />
            </ChakraTooltip>
            <ReportingChartFullscreenButton
                isFullScreen={isFullScreen}
                onToggle={() => setIsFullScreen(v => !v)}
            />
        </Flex>
    );

    const hasChartData = chartData.length > 0;
    const isRefetching = isFetching && hasChartData;

    const showHeaderSpinner = isRefetching;
    const chartHeader = (
        <Flex justify="space-between" align="center" w="100%" wrap="wrap" gap={2}>
            <Flex align="center" gap={1.5} minW={0} flexShrink={1}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.600">{chartTitle}</Text>
                {showHeaderSpinner && <Spinner size="xs" color="#0071E3" flexShrink={0} />}
                {metricTooltip && (
                    <ChakraTooltip label={metricTooltip} placement="top" hasArrow>
                        <Box as="span" display="inline-flex" color="gray.400" _hover={{ color: 'gray.600' }} cursor="help">
                            <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />
                        </Box>
                    </ChakraTooltip>
                )}
            </Flex>
            {metricSelectorRow}
        </Flex>
    );

    const renderChartShell = (body: ReactNode, bodyOpacity?: number) => (
        <ReportingFullScreenShell
            isOpen={isFullScreen}
            onClose={() => setIsFullScreen(false)}
            fullscreenToolbar={isFullScreen ? chartHeader : undefined}
        >
            <Box h="100%" display="flex" flexDirection="column">
                {tsSettingsDrawerContent}
                {!isFullScreen && (
                    <Box px={4} py={2.5} flexShrink={0} bg="gray.50">
                        {chartHeader}
                    </Box>
                )}
                <Box
                    flex={1}
                    minH={0}
                    p={3}
                    display="flex"
                    flexDirection="column"
                    opacity={bodyOpacity}
                    transition="opacity 0.15s"
                >
                    {body}
                </Box>
            </Box>
        </ReportingFullScreenShell>
    );

    if ((isFetching || isLoading) && !hasChartData) {
        return renderChartShell(
            <Skeleton height="100%" borderRadius="md" />,
        );
    }

    if (chartData.length === 0) {
        return renderChartShell(
            <Flex flex={1} align="center" justify="center">
                <Text color="gray.500">No data available</Text>
            </Flex>,
        );
    }

    const minDataValue = displayedValues.length > 0 ? Math.min(...displayedValues) : 0;
    const maxDataValue = displayedValues.length > 0 ? Math.max(...displayedValues) : 0;

    const axisLabels = chartData.map((row) => String(row.displayDate ?? ''));
    const maxAxisLabelLen = axisLabels.reduce((max, label) => Math.max(max, label.length), 0);
    const plotWidthPx = chartContainerWidth > 0
        ? getTimeseriesPlotWidth(chartContainerWidth, yAxisWidth(isFullScreen))
        : undefined;
    const xAxisLayout = getTimeseriesXAxisLayout(
        granularity,
        chartData.length,
        maxAxisLabelLen,
        isFullScreen,
        plotWidthPx,
        axisLabels,
    );
    const domainValueFont = chartValueFontSize(isFullScreen, true, 11);
    const domainYAxisFont = yAxisTickFontSize(isFullScreen, 11);
    const axisWidth = yAxisWidth(isFullScreen);

    // Reserve space below negative bars for outside value labels (same as ranking / Overview).
    const timeseriesBarTopMargin = 32;
    const plotHeightPx = isFullScreen
        ? Math.max(300, (typeof window !== 'undefined' ? window.innerHeight : 800) - 266)
        : Math.max(0, 280 - timeseriesBarTopMargin - xAxisLayout.bottom - xAxisLayout.height);
    const [yMin, yMax] = getBarChartYDomain(minDataValue, maxDataValue, {
        format,
        capPercentMaxAt100: false,
    }, plotHeightPx);

    return renderChartShell(
        <Box ref={chartWidthRef} flex={1} minH={0} w="100%" h="100%">
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={chartData}
                margin={{ top: 32, right: 10, left: 0, bottom: xAxisLayout.bottom }}
                barGap={hasComparisonBarData ? 4 : undefined}
                barCategoryGap={hasComparisonBarData ? '20%' : undefined}
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                    dataKey="displayDate"
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                    angle={xAxisLayout.angle}
                    textAnchor={xAxisLayout.textAnchor}
                    height={xAxisLayout.height}
                    tickMargin={xAxisLayout.tickMargin}
                    padding={{ left: 0, right: xAxisLayout.labelPadX }}
                    interval={0}
                    tick={timeseriesXAxisTickRenderer(xAxisLayout, chartData.length, chartContainerWidth)}
                />
                <YAxis
                    domain={[yMin, yMax]}
                    ticks={getBarChartYTicks(yMin, yMax)}
                    tick={{ fontSize: domainYAxisFont, fill: '#718096' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => (v === 0 && yMin >= 0 ? '' : formatChartValue(v, format))}
                    width={axisWidth}
                    interval={0}
                    allowDecimals={format === 'percent'}
                />
                <RechartsTooltip
                    content={renderClampedTooltip}
                    position={tooltipPosition}
                    wrapperStyle={{ zIndex: 20 }}
                    cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                />

                {yMin < 0 && <ReferenceLine y={0} stroke="#CBD5E0" strokeDasharray="3 3" />}

                {selectedEntityIds.length > 0 ? (
                    <>
                        {hasComparisonBarData && entityNames.map((name) => (
                            <Bar
                                key={`prior-${name}`}
                                dataKey={`prior_${name}`}
                                fill="#CBD5E0"
                                radius={[2, 2, 0, 0]}
                                fillOpacity={0.9}
                            >
                                <LabelList
                                    dataKey={`prior_${name}`}
                                    content={comparisonBarLabelListContent((v) => formatChartValue(v, format), domainValueFont)}
                                />
                            </Bar>
                        ))}
                        {entityNames.map((name, index) => (
                            <Bar
                                key={name}
                                dataKey={name}
                                fill={ENTITY_COLORS[index % ENTITY_COLORS.length]}
                                radius={[2, 2, 0, 0]}
                            >
                                <LabelList
                                    dataKey={name}
                                    {...(hasComparisonBarData
                                        ? { content: comparisonBarLabelListContent((v) => formatChartValue(v, format), domainValueFont) }
                                        : verticalBarTopLabelListProps((v) => formatChartValue(v, format), domainValueFont))}
                                />
                            </Bar>
                        ))}
                    </>
                ) : (
                    <>
                        {hasComparisonBarData && (
                            <Bar
                                dataKey="prior_All"
                                fill="#CBD5E0"
                                radius={[4, 4, 0, 0]}
                                fillOpacity={0.9}
                            >
                                <LabelList
                                    dataKey="prior_All"
                                    content={comparisonBarLabelListContent((v) => formatChartValue(v, format), domainValueFont)}
                                />
                            </Bar>
                        )}
                        <Bar
                            dataKey="All"
                            fill="#0071E3"
                            radius={[4, 4, 0, 0]}
                        >
                            {chartData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill="#0071E3" />
                            ))}
                            <LabelList
                                dataKey="All"
                                {...(hasComparisonBarData
                                    ? { content: comparisonBarLabelListContent((v) => formatChartValue(v, format), domainValueFont) }
                                    : verticalBarTopLabelListProps((v) => formatChartValue(v, format), domainValueFont))}
                            />
                        </Bar>
                    </>
                )}

                {selectedEntityIds.length > 1 && (
                    <Legend
                        wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                        iconType="square"
                        iconSize={10}
                        payload={entityNames.map((name, index) => ({
                            value: name,
                            type: 'square' as const,
                            color: ENTITY_COLORS[index % ENTITY_COLORS.length]
                        }))}
                    />
                )}
            </BarChart>
        </ResponsiveContainer>
        </Box>,
        isRefetching ? 0.55 : 1,
    );
};

export default DomainTimeseriesChart;
